import { createContext, ReactNode, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { api, ApiError, onSessionEnded, User } from './api';

interface AuthState {
  user: User | null;
  checking: boolean;
  // True after the server ended a session that was open here (the login page says so).
  ended: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  passwordChanged: () => void;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [checking, setChecking] = useState(true);
  const [ended, setEnded] = useState(false);
  const userRef = useRef(user);
  userRef.current = user;

  // A request refused with 401 while logged in: the session has ended on the server, so go
  // to the login screen instead of leaving the pages up with "Please log in." in them.
  useEffect(() => {
    onSessionEnded(() => {
      if (!userRef.current) return;
      setUser(null);
      setEnded(true);
    });
    return () => onSessionEnded(null);
  }, []);

  // Phones keep a tab asleep for hours: check the login again when it comes back to the front.
  useEffect(() => {
    const check = () => {
      if (document.visibilityState === 'visible' && userRef.current) api.me().catch(() => {});
    };
    document.addEventListener('visibilitychange', check);
    return () => document.removeEventListener('visibilitychange', check);
  }, []);

  useEffect(() => {
    api.me()
      .then((r) => setUser(r.user))
      .catch((e) => { if (!(e instanceof ApiError && e.status === 401)) console.error(e); })
      .finally(() => setChecking(false));
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    setUser((await api.login(email, password)).user);
    setEnded(false);
  }, []);

  const logout = useCallback(async () => {
    await api.logout().catch(() => {});
    setUser(null);
    setEnded(false);
  }, []);

  const passwordChanged = useCallback(() => setUser((u) => (u ? { ...u, mustChangePassword: false } : u)), []);

  return <AuthContext.Provider value={{ user, checking, ended, login, logout, passwordChanged }}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth outside AuthProvider');
  return ctx;
}
